import * as React from 'react';
import { FirebasePopupProvider, SOCIAL_AUTH_PROVIDER_LABEL } from 'constants/auth';
import FaceBookIcon from 'svg/FaceBookIcon';
import GoogleIcon from 'svg/GoogleIcon';
import { Button, ButtonText } from 'components/Button';
import Modal from 'components/modals/Modal';
import classNames from 'styles/utils/classNames';

interface Props {
  email: string;
  /** The provider the user just tried to sign in with (and which we'll link on success). */
  newProvider: FirebasePopupProvider;
  /** Existing sign-in methods (may be empty under email-enumeration protection). */
  existingMethods: string[];
  isLinking: boolean;
  error: Error | null;
  onLinkWithExistingProvider: (provider: FirebasePopupProvider) => void;
  onLinkWithPassword: (password: string) => void;
  onCancel: () => void;
}

/**
 * Shown when an OAuth sign-in collides with an existing account on the same email. The user
 * verifies with a method they already have, then we attach the new provider. Facebook is an
 * untrusted email provider, so Firebase never auto-links — this manual confirmation is required.
 */
export default function ModalAccountLinking({
  email,
  newProvider,
  existingMethods,
  isLinking,
  error,
  onLinkWithExistingProvider,
  onLinkWithPassword,
  onCancel,
}: Props) {
  const [password, setPassword] = React.useState('');

  const newProviderLabel = SOCIAL_AUTH_PROVIDER_LABEL[newProvider];

  // When methods are unknown (enumeration protection) or unrecognized, fall back to offering the
  // other federated provider + password — the only ways an account on this email could exist.
  const hasGoogle = existingMethods.includes('google.com');
  const hasFacebook = existingMethods.includes('facebook.com');
  const hasPassword = existingMethods.includes('password');
  const useFallback = existingMethods.length === 0 || !(hasGoogle || hasFacebook || hasPassword);

  const showGoogle = useFallback ? newProvider !== 'google' : hasGoogle;
  const showFacebook = useFallback ? newProvider !== 'facebook' : hasFacebook;
  const showPassword = useFallback ? true : hasPassword;

  const inputClassName =
    'input-form bg-color-bg-lightmode-secondary text-color-text-lightmode-primary placeholder-color-text-lightmode-placeholder dark:bg-color-bg-darkmode-secondary dark:text-color-text-darkmode-primary dark:placeholder-color-text-darkmode-placeholder';

  const handlePasswordSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!password || isLinking) {
      return;
    }
    onLinkWithPassword(password);
  };

  return (
    <Modal
      isOpen
      handleClose={() => {
        if (!isLinking) {
          onCancel();
        }
      }}
      classNameMaxWidth="sm:max-w-[28rem]"
      className="sm:rounded-[24px]"
    >
      <div className="flex flex-col px-ds-xl pb-ds-lg pt-ds-xl sm:px-ds-3xl sm:pb-ds-3xl sm:pt-ds-2xl">
        <div className="flex flex-col gap-1.5">
          <h3 className="typography-product-heading-compact text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
            You already have an account
          </h3>
          <p className="typography-product-body text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary">
            <span className="font-semibold text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              {email}
            </span>{' '}
            is already registered. Confirm it&apos;s you to add {newProviderLabel} sign-in.
          </p>
        </div>

        {error && (
          <div className="mt-ds-lg rounded-xl bg-color-bg-lightmode-error px-ds-lg py-ds-sm dark:bg-color-bg-darkmode-error">
            <p className="typography-product-caption text-color-error">{error.message}</p>
          </div>
        )}

        <div className="mt-ds-xl flex flex-col gap-ds-md">
          {showGoogle && (
            <Button
              variant="secondary"
              size="lg"
              type="button"
              disabled={isLinking}
              onClick={() => onLinkWithExistingProvider('google')}
            >
              <span className="flex items-center justify-center gap-ds-sm whitespace-nowrap">
                <GoogleIcon className="h-5 w-5 shrink-0" />
                Continue with Google
              </span>
            </Button>
          )}
          {showFacebook && (
            <Button
              variant="secondary"
              size="lg"
              type="button"
              disabled={isLinking}
              onClick={() => onLinkWithExistingProvider('facebook')}
            >
              <span className="flex items-center justify-center gap-ds-sm whitespace-nowrap">
                <FaceBookIcon className="h-5 w-5 shrink-0 text-[#1877F2]" />
                Continue with Facebook
              </span>
            </Button>
          )}

          {showPassword && (
            <>
              {(showGoogle || showFacebook) && (
                <div className="my-ds-sm flex items-center gap-3">
                  <span className="h-px flex-1 bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />
                  <span className="typography-product-caption text-color-text-lightmode-tertiary dark:text-color-text-darkmode-tertiary">
                    or
                  </span>
                  <span className="h-px flex-1 bg-color-border-input-lightmode dark:bg-color-border-input-darkmode" />
                </div>
              )}
              <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-ds-md">
                <div className="w-full">
                  <label
                    className="typography-product-element-label mb-2 block text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"
                    htmlFor="account-linking-password"
                  >
                    Password
                  </label>
                  <input
                    id="account-linking-password"
                    type="password"
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    value={password}
                    disabled={isLinking}
                    onChange={(event) => setPassword(event.target.value)}
                    className={inputClassName}
                  />
                </div>
                <Button
                  variant="brand"
                  size="lg"
                  type="submit"
                  disabled={isLinking || !password}
                  loading={isLinking}
                >
                  {isLinking ? 'Connecting...' : `Sign in & connect ${newProviderLabel}`}
                </Button>
              </form>
            </>
          )}
        </div>

        <div className={classNames('flex justify-center', showPassword ? 'mt-ds-lg' : 'mt-ds-xl')}>
          <ButtonText
            type="button"
            disabled={isLinking}
            onClick={onCancel}
            className="typography-product-button-label-medium font-normal text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary"
          >
            Cancel
          </ButtonText>
        </div>
      </div>
    </Modal>
  );
}
